import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "../../../../../assets/icons";
import {
  UNDEFINED,
  sharedConstants,
  galleryConstants,
  globalSystemConstants,
  shutterbDashboardConstants,
  EMPTY_STRING,
} from "../../../../../constants";
import {
  customToast,
  convertDateToUTC,
  convertBytesToMbs,
  calculateTotalFileSizeInMbs,
} from "../../../../../shared/utility";
import { shutterbDashboardActions } from "../../../../../redux/actions";
import ViewGallery from "../../../../../shared/components/ViewGallery/ViewGallery";

const ShutterBGallery = (props) => {
  const { t } = useTranslation("common");

  const fileInputRef = useRef(null);
  const currentFilePrevUploadedSizeRef = useRef(0);

  const [files, setFiles] = useState([]);
  const [filesAdded, setFilesAdded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentFileName, setCurrentFileName] = useState("");
  const [currentFileSize, setCurrentFileSize] = useState("");
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [eventDetailsById, setEventDetailsById] = useState(null);
  const [counterFilesUploaded, setCounterFilesUploaded] = useState(1);
  const [totalFileSizeToUpload, setTotalFileSizeToUpload] = useState(0);
  const [totalUploadedFileSize, setTotalUploadedFileSize] = useState(0);
  const [isDeletionInProgress, setIsDeletionInProgress] = useState(false);
  const [currentFileUploadedSize, setCurrentFileUploadedSize] = useState(0);
  const [counterTotalFilesToUpload, setCounterTotalFilesToUpload] = useState(0);

  const handleDelete = async () => {
    const filesToDeleteFromServer = selectedFiles.filter((file) => file.id);
    const filesToRemoveLocally = selectedFiles.filter((file) => !file.id);

    props.isFileDeleted();
    if (filesToDeleteFromServer.length > 0) {
      const idArray = filesToDeleteFromServer.map((file) => file.id);
      props.deleteFromGallery({ ids: idArray });
      setIsDeletionInProgress(true);
    }

    if (filesToRemoveLocally.length > 0) {
      setFiles((prevFiles) =>
        prevFiles.filter((file) => !filesToRemoveLocally.includes(file))
      );
    }

    if (
      filesToRemoveLocally.length > 0 &&
      filesToDeleteFromServer.length === 0
    ) {
      setSelectedFiles([]);
    }
  };

  const handleAddToProfile = async () => {
    props.activateSpinner();
    if (
      selectedFiles.length >
      shutterbDashboardConstants.SB_PORTFOLIO_MAX_IMAGE_COUNT
    ) {
      customToast(t("max_img_error"), toast.TYPE.ERROR);
      props.diActivateSpinner();
      return false;
    }

    // backend call with form data
    let formdata = new FormData();
    formdata.append("sbUserId", props.shutterBId);
    for (let i = 0; i < selectedFiles.length; i++) {
      if (selectedFiles[i].file.name === UNDEFINED) {
        try {
          const fileNameFromUrl = extractFileNameFromUrl(selectedFiles[i].url);
          const response = await fetch(selectedFiles[i].url);
          const blob = await response.blob();
          const fileToSent = new File([blob], fileNameFromUrl, {
            type: blob.type,
          });
          formdata.append("file", fileToSent);
          props.diActivateSpinner();
        } catch (error) {
          console.error(error);
          props.diActivateSpinner();
          return;
        }
      } else {
        formdata.append("file", selectedFiles.file);
      }

      // calling the action on last index
      if (i + 1 === selectedFiles.length) {
        const options = (progressEvent) => {
          const { loaded, total } = progressEvent;
          let percent = Math.floor((loaded * 100) / total);
          if (percent <= 100) {
            setUploadPercentage(percent);
          }
        };
        props.addToProfile(formdata, options);
        setSelectedFiles([]);
      }
    }
  };

  // Helper function to extract the file name from the URL
  const extractFileNameFromUrl = (url) => {
    const parts = url.split("/");
    const fileName = parts.slice(-3).join("/");
    return fileName;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*, video/*, .heic, .heif",
    onDrop: (acceptedFiles) => {
      setFiles((prevFiles) =>
        prevFiles.concat(
          acceptedFiles.map((file) => ({
            file,
            url: URL.createObjectURL(file),
            selected: false,
          }))
        )
      );
    },
  });

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const resetFileInput = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.value = EMPTY_STRING;
    }
  };

  const handleFileInputChange = async (event) => {
    const selectedFiles = event.target.files;
    if (selectedFiles.length > 0) {
      props.activateOverlay();
    }
    // size limit: total payload size limit
    let totalSelectedFilesArr = Array.from(selectedFiles);
    let totalPayloadSize = calculateTotalFileSizeInMbs(totalSelectedFilesArr);
    if (totalPayloadSize > process.env.REACT_APP_GALLERY_UPLOAD_PAYLOAD_LIMIT) {
      customToast(
        `upto ${process.env.REACT_APP_GALLERY_UPLOAD_PAYLOAD_LIMIT} MBs are allowed in an attempt`,
        toast.TYPE.ERROR
      );
      props.diActivateOverlay();
      resetFileInput();
      return null;
    }
    // size limit: each file size limit
    let fileWithInvalidSizeArr = totalSelectedFilesArr.filter((file) => {
      return (
        file.size / sharedConstants.KB_TO_MB_BYTES >=
        process.env.REACT_APP_GALLERY_UPLOAD_FILE_SIZE_LIMIT
      );
    });
    if (fileWithInvalidSizeArr.length > 0) {
      customToast(
        `each file size should be under ${process.env.REACT_APP_GALLERY_UPLOAD_FILE_SIZE_LIMIT}MB.`,
        toast.TYPE.ERROR
      );
      props.diActivateOverlay();
      resetFileInput();
      return null;
    }
    const convertedFiles = Array.from(selectedFiles).map((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (
        !file.type.includes("image") &&
        !file.type.includes("video") &&
        fileExtension !== "heic" &&
        fileExtension !== "heif"
      ) {
        customToast("file format not supported", toast.TYPE.ERROR);
        props.diActivateOverlay();
        resetFileInput();
        return null;
      }

      return {
        file,
        url: sharedConstants.IS_GALLERY_UPLOAD_PREVIEW,
        selected: false,
      };
    });

    // Filter out any null entries from unsupported formats
    const validFiles = convertedFiles.filter((file) => file !== null);

    setFiles((prevFiles) => {
      setFilesAdded(true); // Set the flag to true when files are added
      return [...prevFiles, ...validFiles];
    });
    resetFileInput();
  };

  const handleAddToGallery = () => {
    let formdata = new FormData();
    const filesToSend = files.filter((file) => !file.id);
    filesToSend.forEach((file) => {
      formdata.append("file", file.file);
    });
    formdata.append("eventId", props.eventId);
    formdata.append("SbUserId", props.shutterBId);
    formdata.append("contentType", "file");
    if (filesToSend.length > 0) {
      const options = (progressEvent) => {
        const { loaded, total } = progressEvent;
        let percent = Math.floor((loaded * 100) / total);
        if (percent <= 100) {
          setCurrentFileUploadedSize(loaded);
        }
      };

      props.addToGallery(
        formdata,
        options,
        null,
        counterFilesUploaded,
        setCounterFilesUploaded,
        totalUploadedFileSize,
        setTotalFileSizeToUpload,
        currentFilePrevUploadedSizeRef,
        setCurrentFileName,
        setCurrentFileSize
      );
      setCounterFilesUploaded(1);
    } else {
      //customToast(t("files_already_uploaded"), toast.TYPE.ERROR);
    }
    setSelectedFiles([]);
  };

  useEffect(() => {
    setTotalUploadedFileSize(
      totalUploadedFileSize +
        convertBytesToMbs(currentFileUploadedSize) -
        convertBytesToMbs(currentFilePrevUploadedSizeRef.current)
    );
    // keeping percentage in float to pass this condition progress > 0 and to show it as soon as uploading starts
    let updatedPercentage = (
      ((totalUploadedFileSize +
        convertBytesToMbs(currentFileUploadedSize) -
        convertBytesToMbs(currentFilePrevUploadedSizeRef.current)) /
        totalFileSizeToUpload) *
      100
    ).toFixed(2);
    updatedPercentage <= 100 && updatedPercentage > 0
      ? setUploadPercentage(updatedPercentage)
      : setUploadPercentage(100);

    currentFilePrevUploadedSizeRef.current = currentFileUploadedSize;
    // eslint-disable-next-line
  }, [currentFileUploadedSize]);

  useEffect(() => {
    if (isDeletionInProgress && props.isImageDeleted) {
      const filesToDeleteFromServer = selectedFiles.filter((file) => file.id);
      if (filesToDeleteFromServer.length > 0) {
        setFiles((prevFiles) =>
          prevFiles.filter((file) => !filesToDeleteFromServer.includes(file))
        );
        setSelectedFiles([]);
      }
      setIsDeletionInProgress(false);
    }
    // eslint-disable-next-line
  }, [isDeletionInProgress, props.isImageDeleted]);

  useEffect(() => {
    setUploadPercentage(0);

    // eslint-disable-next-line
  }, [props.isAddedToProfile]);

  useEffect(() => {
    if (filesAdded) {
      setFilesAdded(false);
      // Call handleAddToGallery only if files have been added
      handleAddToGallery();
    }
    // eslint-disable-next-line
  }, [files]);

  useEffect(() => {
    if (props.eventDetails) {
      const allSubEvents = props.eventDetails.flatMap(
        (group) => group.subEvents || []
      );
      const matchedEvent = allSubEvents?.find(
        (event) => event.id === props.eventId
      );

      setEventDetailsById(matchedEvent);
    }
    // eslint-disable-next-line
  }, [props.eventDetails]);

  useEffect(() => {
    setUploadPercentage(0);
    setTotalFileSizeToUpload(0);
    setTotalUploadedFileSize(0);
    currentFilePrevUploadedSizeRef.current = 0;
    const data = props.galleryData?.gallery;
    if (data) {
      const updatedFiles = data.map((file) => ({
        ...file,
        imageUrl: `${process.env.REACT_APP_S3_BASE_URL_EVENTS_GALLERY}/${file.imageUrl}`,
        thumbnailUrl: `${process.env.REACT_APP_S3_BASE_URL_EVENTS_GALLERY}/${file.thumbnailUrl}`,
      }));
      setFiles(
        updatedFiles.map((file) => {
          let fileType;
          let galleryType;
          if (
            file.imageUrl.endsWith(".mp4") ||
            file.imageUrl.endsWith(".MP4")
          ) {
            fileType = "video/mp4";
            galleryType = galleryConstants.GALLERY_VIDEOS;
          } else if (
            file.imageUrl.endsWith(".mov") ||
            file.imageUrl.endsWith(".MOV")
          ) {
            fileType = "video/mov";
            galleryType = galleryConstants.GALLERY_VIDEOS;
          } else if (
            file.imageUrl.endsWith(".m4v") ||
            file.imageUrl.endsWith(".M4V")
          ) {
            fileType = "video/m4v";
            galleryType = galleryConstants.GALLERY_VIDEOS;
          } else {
            fileType = "image/jpeg";
            galleryType = galleryConstants.GALLERY_IMAGES;
          }

          return {
            file: new File([file], file.name, {
              type: fileType,
              lastModified: file.lastModified,
              lastModifiedDate: file.lastModifiedDate,
            }),
            url: file.imageUrl,
            thumbnailUrl: file.thumbnailUrl,
            selected: false,
            id: file?.id,
            isFavorite: file?.isFavorite,
            contentType: file?.contentType,
            galleryType:
              file?.contentType === galleryConstants.CONTENT_TYPE_REELS
                ? galleryConstants.GALLERY_REELS
                : galleryType,
          };
        })
      );
    }
  }, [props.galleryData]);

  const favoriteClipHandle = (id) => {
    props.favoriteClip(id, () => props.handleGalleryClick(props.eventId));
  };

  return (
    <>
      <div className="row d-flex align-items-center justify-content-between pl-3 pr-3 mb-4">
        <div className="d-flex align-items-center justify-content-center">
          <button
            type="button"
            className="back-button-sb cursor-pointer"
            onClick={() => props.setSelectedEventId(null)}
          >
            <Icons.BackIcon className="back-icon" />
          </button>
          {props.galleryData.eventName ? (
            <p className="m-0">
              {props.galleryData.eventName} -{" "}
              {props.galleryData.eventDate
                ? convertDateToUTC(
                    props.galleryData.eventDate,
                    props.galleryData?.eventLocations?.[0]?.timezone
                  )
                : ""}
            </p>
          ) : (
            ""
          )}
        </div>
        <p className="m-0">
          <span className="bold-text">Event ID:</span> {props.galleryData.id}
        </p>
      </div>
      <div>
        <div className="row">
          <div className="col-12 d-flex flex-wrap g-2">
            <button
              disabled={
                props.isSubEventGalleryView ||
                selectedFiles.length === 0 ||
                uploadPercentage > 0
              }
              className="button-common button-fill btn-selection"
              onClick={() => handleAddToProfile()}
            >
              add to profile
            </button>
            <span {...getRootProps()} className="dropzone-content">
              <input
                {...getInputProps()}
                ref={fileInputRef}
                disabled={uploadPercentage > 0}
                onChange={handleFileInputChange}
              />
              <button
                className="button-common button-fill btn-selection"
                disabled={props.isSubEventGalleryView || uploadPercentage > 0}
                onClick={handleButtonClick}
              >
                upload files
              </button>
            </span>
          </div>
        </div>
        {files.length >= 0 &&
          typeof props.galleryData?.gallery !== "undefined" && (
            <ViewGallery
              files={files}
              isUploadAllowed
              isModalAllowed
              isAsyncUploadCall
              setFiles={setFiles}
              handleDelete={handleDelete}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              progress={uploadPercentage}
              counterFilesUploaded={counterFilesUploaded}
              counterTotalFilesToUpload={counterTotalFilesToUpload}
              setCounterTotalFilesToUpload={setCounterTotalFilesToUpload}
              setCounterFilesUploaded={setCounterFilesUploaded}
              currentFileName={currentFileName}
              setCurrentFileName={setCurrentFileName}
              currentFileSize={currentFileSize}
              setCurrentFileSize={setCurrentFileSize}
              isTabsDisplay
              favoriteClipHandle={favoriteClipHandle}
              showHearthIcon
              hideDeleteIcon={props.isSubEventGalleryView}
            />
          )}
        <div className="col-12 d-flex justify-content-center pt-4 px-0">
          <button
            className="button-common button-fill btn-selection mb-3 ml-0"
            disabled={
              props.isSubEventGalleryView ||
              eventDetailsById?.orderItems[0].checkedStatus !==
                shutterbDashboardConstants.CHECK_IN
            }
            onClick={(e) =>
              props.onCheckStatus(
                eventDetailsById?.id,
                shutterbDashboardConstants.CHECK_OUT,
                props.setSelectedEventId
              )
            }
          >
            checkout
          </button>
        </div>
      </div>
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  galleryData: state.shutterbDashboard.galleryData,
  shutterBId: state.shutterbAuthentication.sbUserId,
  isImageDeleted: state.shutterbDashboard.isImageDeleted,
  isAddedToProfile: state.shutterbDashboard.isAddedToProfile,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  favoriteClip: (clipId, getUpdatedData) =>
    dispatch(shutterbDashboardActions.favoriteClip(clipId, getUpdatedData)),
  deleteFromGallery: (data) =>
    dispatch(shutterbDashboardActions.deleteFromGallery(data)),
  addToGallery: (
    data,
    options,
    dummy,
    counterFilesUploaded,
    setCounterFilesUploaded,
    totalUploadedFileSize,
    setTotalFileSizeToUpload,
    currentFilePrevUploadedSizeRef,
    setCurrentFileName,
    setCurrentFileSize
  ) =>
    dispatch(
      shutterbDashboardActions.addToGallery(
        data,
        options,
        dummy,
        counterFilesUploaded,
        setCounterFilesUploaded,
        totalUploadedFileSize,
        setTotalFileSizeToUpload,
        currentFilePrevUploadedSizeRef,
        setCurrentFileName,
        setCurrentFileSize
      )
    ),
  addToProfile: (data, options) =>
    dispatch(shutterbDashboardActions.addToProfile(data, options)),
  isFileDeleted: () => dispatch(shutterbDashboardActions.isFileDeleted()),
  activateOverlay: () =>
    dispatch({ type: globalSystemConstants.OVERLAY_ACTIVATE, data: null }),
  diActivateOverlay: () =>
    dispatch({ type: globalSystemConstants.OVERLAY_DIACTIVATE, data: null }),
  activateSpinner: () =>
    dispatch({ type: globalSystemConstants.SPINNER_ACTIVATE, data: null }),
  diActivateSpinner: () =>
    dispatch({ type: globalSystemConstants.SPINNER_DIACTIVATE, data: null }),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ShutterBGallery));
